import React, { useMemo, useState } from 'react';
import { Box, Typography, IconButton, Popover, List, ListItemButton } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import dayjs, { Dayjs } from 'dayjs';
import { ReactComponent as CalendarIcon } from '../../../assets/PepWise2.0/Calendar.svg';
import { ReactComponent as CalendarIconActive } from '../../../assets/PepWise2.0/Calendar-Active.svg';
import { ReactComponent as RightArrowBoxIcon } from '../../../assets/PepWise2.0/Right-Arrow-Icon-Box.svg';
import { ReactComponent as LeftArrowBoxIcon } from '../../../assets/PepWise2.0/Left-Arrow-Icon-Box.svg';

const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const presets = [
  'Today',
  'Last 24 Hrs',
  'Last 7 Days',
  'Last 30 Days',
  'Last 3 Months',
  'Custom Date Range',
];

type Props = {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  onChange: (range: { startDate: Dayjs | null; endDate: Dayjs | null }) => void;
  minDate?: Dayjs | null;
  maxDate?: Dayjs | null;
};

const getMonthMatrix = (month: Dayjs) => {
  const start = month.startOf('month').startOf('week');
  const end = month.endOf('month').endOf('week');

  let current = start.clone();
  const matrix: Dayjs[][] = [];

  while (current.isBefore(end) || current.isSame(end, 'day')) {
    const week: Dayjs[] = [];

    for (let i = 0; i < 7; i++) {
      week.push(current.clone());
      current = current.add(1, 'day');
    }

    matrix.push(week);
  }

  return matrix;
};

export default function DateFilter({
  startDate,
  endDate,
  onChange,
  minDate = null,
  maxDate = null,
}: Props) {
  const theme = useTheme();
  const colors = theme.palette;

  const today = dayjs().endOf('day');
  const effectiveMaxDate = useMemo(() => {
    if (!maxDate) return today;
    return maxDate.isBefore(today, 'day') ? maxDate.endOf('day') : today;
  }, [maxDate, today]);

  const effectiveMinDate = useMemo(() => {
    return minDate ? minDate.startOf('day') : null;
  }, [minDate]);

  const initialMonth = useMemo(() => {
    if (startDate) return startDate;
    if (effectiveMaxDate) return effectiveMaxDate;
    return dayjs();
  }, [startDate, effectiveMaxDate]);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [month, setMonth] = useState(initialMonth);
  const [showCalendar, setShowCalendar] = useState(false);
  const [hoverDate, setHoverDate] = useState<Dayjs | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<string>('');

  const open = Boolean(anchorEl);
  const matrix = getMonthMatrix(month);

  const clampDate = (date: Dayjs) => {
    let next = date;
    if (effectiveMinDate && next.isBefore(effectiveMinDate, 'day')) {
      next = effectiveMinDate;
    }
    if (effectiveMaxDate && next.isAfter(effectiveMaxDate, 'day')) {
      next = effectiveMaxDate;
    }
    return next;
  };

  const isDisabledDate = (date: Dayjs) => {
    if (effectiveMinDate && date.isBefore(effectiveMinDate, 'day')) return true;
    if (effectiveMaxDate && date.isAfter(effectiveMaxDate, 'day')) return true;
    return false;
  };

  const canGoPrevMonth = useMemo(() => {
    if (!effectiveMinDate) return true;
    const prevMonth = month.subtract(1, 'month');
    return !prevMonth.endOf('month').isBefore(effectiveMinDate, 'day');
  }, [month, effectiveMinDate]);

  const canGoNextMonth = useMemo(() => {
    const nextMonth = month.add(1, 'month');
    return !nextMonth.startOf('month').isAfter(effectiveMaxDate, 'day');
  }, [month, effectiveMaxDate]);

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setShowCalendar(false);
    setHoverDate(null);
  };

  const applyPreset = (preset: string) => {
    setSelectedPreset(preset);

    if (preset === 'Today') {
      const date = clampDate(dayjs());
      onChange({ startDate: date, endDate: date });
      handleClose();
      return;
    }

    if (preset === 'Last 24 Hrs') {
      const end = clampDate(dayjs());
      const start = clampDate(dayjs().subtract(1, 'day'));
      onChange({
        startDate: start.isAfter(end, 'day') ? end : start,
        endDate: end,
      });
      handleClose();
      return;
    }

    if (preset === 'Last 7 Days') {
      const end = clampDate(dayjs());
      const start = clampDate(dayjs().subtract(7, 'day'));
      onChange({
        startDate: start.isAfter(end, 'day') ? end : start,
        endDate: end,
      });
      handleClose();
      return;
    }

    if (preset === 'Last 30 Days') {
      const end = clampDate(dayjs());
      const start = clampDate(dayjs().subtract(30, 'day'));
      onChange({
        startDate: start.isAfter(end, 'day') ? end : start,
        endDate: end,
      });
      handleClose();
      return;
    }

    if (preset === 'Last 3 Months') {
      const end = clampDate(dayjs());
      const start = clampDate(dayjs().subtract(3, 'month'));
      onChange({
        startDate: start.isAfter(end, 'day') ? end : start,
        endDate: end,
      });
      handleClose();
      return;
    }

    if (preset === 'Custom Date Range') {
      setShowCalendar(true);
      setMonth(clampDate(startDate || endDate || dayjs()));
    }
  };

  const handleDateClick = (date: Dayjs) => {
    if (isDisabledDate(date)) return;

    if (!startDate || endDate) {
      onChange({ startDate: date, endDate: null });
      return;
    }

    if (date.isBefore(startDate, 'day')) {
      onChange({ startDate: date, endDate: startDate });
    } else {
      onChange({ startDate, endDate: date });
    }
  };

  const inRange = (date: Dayjs) => {
    if (isDisabledDate(date)) return false;

    if (startDate && !endDate && hoverDate && !isDisabledDate(hoverDate)) {
      const min = hoverDate.isBefore(startDate) ? hoverDate : startDate;
      const max = hoverDate.isBefore(startDate) ? startDate : hoverDate;

      return (
        (date.isAfter(min, 'day') || date.isSame(min, 'day')) &&
        (date.isBefore(max, 'day') || date.isSame(max, 'day'))
      );
    }

    if (startDate && endDate) {
      return (
        (date.isAfter(startDate, 'day') || date.isSame(startDate, 'day')) &&
        (date.isBefore(endDate, 'day') || date.isSame(endDate, 'day'))
      );
    }

    return false;
  };

  const isStart = (d: Dayjs) => !!startDate && d.isSame(startDate, 'day');
  const isEnd = (d: Dayjs) => !!endDate && d.isSame(endDate, 'day');

  const label =
    selectedPreset === 'Today'
      ? 'Today'
      : startDate && endDate
      ? `${startDate.format('DD-MM-YYYY')} to ${endDate.format('DD-MM-YYYY')}`
      : dayjs().format('MMM YYYY');

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      sx={{
        px: 1,
        '&:hover': {
          backgroundColor: colors.dateFilter.triggerHoverBackground,
        },
      }}
    >
      <Typography
        fontSize="14px"
        sx={{
          color: open ? colors.dateFilter.triggerTextActive : colors.dateFilter.triggerText,
        }}
      >
        {label}
      </Typography>

      <IconButton
        onClick={handleClick}
        sx={{
          p: 0.5,
        }}
      >
        {open ? <CalendarIconActive /> : <CalendarIcon />}
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: `${colors.dateFilter.popoverBackground} !important`,
              backgroundImage: 'none !important',
              color: `${colors.dateFilter.popoverText} !important`,
              borderRadius: '8px',
              boxShadow: colors.dateFilter.popoverShadow,
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            background: `${colors.dateFilter.popoverBackground} !important`,
          }}
        >
          <Box
            sx={{
              width: 232,
              borderRight: `1px solid ${colors.dateFilter.popoverBorder}`,
              background: `${colors.dateFilter.presetPanelBackground} !important`,
            }}
          >
            <List sx={{ p: 0 }}>
              {presets.map((p) => (
                <ListItemButton
                  key={p}
                  selected={selectedPreset === p}
                  onClick={() => applyPreset(p)}
                  sx={{
                    fontSize: '14px',
                    height: '44px',
                    fontWeight: 400,
                    color: `${colors.dateFilter.presetText} !important`,
                    '&.Mui-selected': {
                      background: `${colors.dateFilter.presetSelectedBackground} !important`,
                    },
                    '&:hover': {
                      background: `${colors.dateFilter.presetHoverBackground} !important`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography fontSize="14px">{p}</Typography>

                    {p === 'Custom Date Range' && (
                      <ChevronRight
                        sx={{
                          fontSize: 18,
                          color: colors.dateFilter.presetArrow,
                        }}
                      />
                    )}
                  </Box>
                </ListItemButton>
              ))}
            </List>
          </Box>

          {showCalendar && (
            <Box
              sx={{
                p: 2,
                width: 260,
                background: `${colors.dateFilter.calendarBackground} !important`,
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <IconButton
                  size="small"
                  onClick={() => canGoPrevMonth && setMonth(month.subtract(1, 'month'))}
                  disabled={!canGoPrevMonth}
                >
                  <RightArrowBoxIcon />
                </IconButton>

                <Typography fontWeight={600} sx={{ color: colors.dateFilter.calendarHeaderText }}>
                  {month.format('MMM, YYYY')}
                </Typography>

                <IconButton
                  size="small"
                  onClick={() => canGoNextMonth && setMonth(month.add(1, 'month'))}
                  disabled={!canGoNextMonth}
                >
                  <LeftArrowBoxIcon />
                </IconButton>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', mb: 1 }}>
                {weekDays.map((d) => (
                  <Typography
                    key={d}
                    fontSize={12}
                    textAlign="center"
                    sx={{ color: colors.dateFilter.weekDayText }}
                  >
                    {d}
                  </Typography>
                ))}
              </Box>

              {matrix.map((week, i) => (
                <Box key={i} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)' }}>
                  {week.map((date) => {
                    const start = isStart(date);
                    const end = isEnd(date);
                    const range = inRange(date);
                    const disabledDate = isDisabledDate(date);
                    const outsideMonth = !date.isSame(month, 'month');

                    return (
                      <Box
                        key={date.toString()}
                        onClick={() => handleDateClick(date)}
                        onMouseEnter={() => {
                          if (!disabledDate) setHoverDate(date);
                        }}
                        sx={{
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: disabledDate ? 'not-allowed' : 'pointer',
                          backgroundColor: range
                            ? colors.dateFilter.rangeBackground
                            : 'transparent',
                          borderTopLeftRadius: start ? '4px' : 0,
                          borderBottomLeftRadius: start ? '4px' : 0,
                          borderTopRightRadius: end ? '4px' : 0,
                          borderBottomRightRadius: end ? '4px' : 0,
                          opacity: disabledDate ? 0.35 : outsideMonth ? 0.65 : 1,
                          pointerEvents: 'auto',
                          '&:hover': {
                            backgroundColor: disabledDate
                              ? 'transparent'
                              : `${colors.dateFilter.dayHoverBackground} !important`,
                            color: disabledDate
                              ? 'inherit'
                              : `${colors.dateFilter.dayHoverText} !important`,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            backgroundColor: 'transparent',
                            color: disabledDate
                              ? colors.dateFilter.dayText
                              : range
                              ? colors.dateFilter.dayTextInRange
                              : colors.dateFilter.dayText,
                            '&:hover': {
                              color: disabledDate
                                ? colors.dateFilter.dayText
                                : `${colors.dateFilter.dayHoverText} !important`,
                            },
                          }}
                        >
                          {date.date()}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Popover>
    </Box>
  );
}
